// src/services/slowlorisProtection.js

class SlowlorisProtectionService {
  constructor() {
    this.requestQueue = new Map();
    this.connectionLimits = {
      maxConcurrentRequests: 15,
      maxRequestsPerMinute: 100,
      requestTimeout: 45000,
      connectionTimeout: 15000,
    };
    this.monitoring = {
      requestCounts: new Map(),
      suspiciousIPs: new Set(),
      blockedIPs: new Set(),
      slowConnections: new Map(),
    };
    this._startMonitoring();
  }

  _startMonitoring() {
    setInterval(() => this._cleanupOldRequests(), 60000);
    setInterval(() => this._monitorSlowConnections(), 30000);
  }

  // Auth endpoints that should always be allowed through
  _isAuthRequest(url) {
    const bypassPatterns = [
    '/auth/sso-login',
    '/auth/me',
    '/auth/logout',
    'login.microsoftonline.com',
    '/batch',          // staging server API key route, not browser traffic
    '/verify-assets',  // staging server API key route, not browser traffic
  ];
    return bypassPatterns.some((p) => url.includes(p));
  }

  _getClientId() {
    let clientId = sessionStorage.getItem('ams_client_id');
    if (!clientId) {
      const arr = new Uint8Array(8);
      crypto.getRandomValues(arr);
      clientId = `client_${Date.now()}_${Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')}`;
      sessionStorage.setItem('ams_client_id', clientId);
    }
    return clientId;
  }

  _generateRequestId() {
    const arr = new Uint8Array(8);
    crypto.getRandomValues(arr);
    return `req_${Date.now()}_${Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  }

  _checkRateLimit(clientId) {
    const now = Date.now();
    const windowStart = now - 60000;

    if (!this.monitoring.requestCounts.has(clientId)) {
      this.monitoring.requestCounts.set(clientId, []);
    }

    const requests = this.monitoring.requestCounts.get(clientId);
    const recent = requests.filter((t) => t > windowStart);
    this.monitoring.requestCounts.set(clientId, recent);

    if (recent.length >= this.connectionLimits.maxRequestsPerMinute) {
      this._detectPattern(clientId, 'rate_limit_exceeded');
      return false;
    }

    recent.push(now);
    return true;
  }

  _checkConcurrentConnections(clientId) {
    const active = Array.from(this.requestQueue.values()).filter(
      (r) => r.clientId === clientId && r.active
    ).length;

    if (active >= this.connectionLimits.maxConcurrentRequests) {
      this._detectPattern(clientId, 'concurrent_limit_exceeded');
      return false;
    }
    return true;
  }

  _trackStart(requestId, clientId) {
    this.requestQueue.set(requestId, { clientId, startTime: Date.now(), active: true });
  }

  _trackComplete(requestId, clientId, success) {
    const req = this.requestQueue.get(requestId);
    if (!req) return;

    req.active = false;
    req.endTime = Date.now();
    req.duration = req.endTime - req.startTime;
    req.success = success;

    if (req.duration > this.connectionLimits.connectionTimeout) {
      this._detectPattern(clientId, 'slow_request');
    }

    setTimeout(() => this.requestQueue.delete(requestId), 60000);
  }

  _detectPattern(clientId, pattern) {
    this.monitoring.suspiciousIPs.add(clientId);

    let count = 0;
    if (this.monitoring.suspiciousIPs.has(clientId)) count++;
    const active = Array.from(this.requestQueue.values()).filter(
      (r) => r.clientId === clientId && r.active
    ).length;
    if (active > this.connectionLimits.maxConcurrentRequests / 2) count++;
    const recent = this.monitoring.requestCounts.get(clientId) || [];
    if (recent.length > this.connectionLimits.maxRequestsPerMinute / 2) count++;

    if (count >= 3) {
      this._blockClient(clientId, 'Multiple suspicious patterns detected');
    }

    this._logSecurityEvent('slowloris_pattern_detected', { clientId, pattern });
  }

  _blockClient(clientId, reason) {
    this.monitoring.blockedIPs.add(clientId);
    console.error(`[SlowlorisProtection] Client blocked: ${clientId} - ${reason}`);

    setTimeout(() => {
      this.monitoring.blockedIPs.delete(clientId);
      this.monitoring.suspiciousIPs.delete(clientId);
    }, 5 * 60 * 1000);

    this._logSecurityEvent('client_blocked', { clientId, reason });
  }

  _monitorSlowConnections() {
    const now = Date.now();
    for (const [requestId, req] of this.requestQueue.entries()) {
      if (req.active && now - req.startTime > this.connectionLimits.connectionTimeout) {
        this.monitoring.slowConnections.set(requestId, {
          clientId: req.clientId,
          duration: now - req.startTime,
          timestamp: now,
        });
        this._detectPattern(req.clientId, 'persistent_slow_connection');
      }
    }
  }

  _cleanupOldRequests() {
    const now = Date.now();
    const threshold = 5 * 60 * 1000;

    for (const [clientId, requests] of this.monitoring.requestCounts.entries()) {
      const recent = requests.filter((t) => now - t < threshold);
      if (recent.length === 0) this.monitoring.requestCounts.delete(clientId);
      else this.monitoring.requestCounts.set(clientId, recent);
    }

    for (const [id, conn] of this.monitoring.slowConnections.entries()) {
      if (now - conn.timestamp > threshold) this.monitoring.slowConnections.delete(id);
    }
  }

  _logSecurityEvent(event, details) {
    const entry = {
      event,
      details,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };
    if (event.includes('blocked') || event.includes('suspicious')) {
      console.warn('[SECURITY ALERT] SlowlorisProtection:', entry);
    }
  }

  /**
   * Main method — wraps an axios config object with timeout + protection checks.
   * Usage: pass the result as axios config, e.g. api(protectedConfig(config))
   * @param {Object} axiosConfig - axios request config
   * @returns {Object} - enhanced axios config with CancelToken
   */
  protectAxiosConfig(axiosConfig = {}) {
    const url = axiosConfig.url || '';

    // Always allow auth endpoints through
    if (this._isAuthRequest(url)) return axiosConfig;

    const clientId = this._getClientId();

    if (this.monitoring.blockedIPs.has(clientId)) {
      throw new Error('Request blocked due to suspicious activity');
    }

    if (!this._checkRateLimit(clientId)) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    if (!this._checkConcurrentConnections(clientId)) {
      throw new Error('Too many concurrent requests. Please wait.');
    }

    const requestId = this._generateRequestId();
    this._trackStart(requestId, clientId);

    // Attach timeout
    const config = {
      ...axiosConfig,
      timeout: axiosConfig.timeout || this.connectionLimits.requestTimeout,
      // Store requestId so the interceptor can complete tracking
      _slowlorisRequestId: requestId,
      _slowlorisClientId: clientId,
    };

    return config;
  }

  completeRequest(requestId, clientId, success) {
    this._trackComplete(requestId, clientId, success);
  }

  getProtectionStatus() {
    return {
      activeConnections: this.requestQueue.size,
      suspiciousClients: this.monitoring.suspiciousIPs.size,
      blockedClients: this.monitoring.blockedIPs.size,
      slowConnections: this.monitoring.slowConnections.size,
    };
  }
}

export const slowlorisProtection = new SlowlorisProtectionService();
export default slowlorisProtection;
